import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { MenuItem, TextField } from '@mui/material';

import { parseNoteBox } from '../../utils/functions';
import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Delete, Get, Post } from '../../Axios/AxiosFunctions';
import Loader from '../../Component/Loader';
import NoData from '../../Component/NoData/NoData';
import { DropDown } from '../../Component/DropDown/DropDown';
import { Button } from '../../Component/Button/Button';
import AreYouSureModal from '../../modals/AreYouSureModal';
import BVIEditorModal from '../../modals/BVIEditorModal';
import classes from './BviViewer.module.css';

const FieldSection = ({ name, value }) => {
  return (
    <div>
      <div className={classes.headline}>{name}</div>
      {Array.isArray(value) ? (
        <ol className={classes.textContainer}>
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ol>
      ) : (
        <div className={classes.textContainer}>{value}</div>
      )}
    </div>
  );
};

const PrintableBvi = ({ innerRef, companyName, seller, fields }) => {
  const sellerName = seller
    ? `${seller?.firstName || ''} ${seller?.lastName || ''}`.trim()
    : '';

  return (
    <div
      ref={innerRef}
      style={{
        padding: '40px',
        color: '#1a1a1a',
        fontFamily: 'Open Sans, Arial, sans-serif',
        fontSize: '13px',
        lineHeight: '1.5',
      }}
    >
      {companyName && (
        <h1
          style={{
            fontSize: '22px',
            margin: '0 0 4px 0',
            color: '#0b2545',
            textTransform: 'uppercase',
          }}
        >
          {companyName}
        </h1>
      )}
      <h2 style={{ fontSize: '15px', margin: '0 0 4px 0', color: '#0b2545' }}>
        Business Value Indicator (BVI)
      </h2>
      {sellerName && (
        <div style={{ margin: '0 0 20px 0', color: '#555' }}>
          Seller: {sellerName}
        </div>
      )}

      {fields?.map((field, index) => (
        <div
          key={index}
          style={{ marginBottom: '14px', breakInside: 'avoid' }}
        >
          <div
            style={{
              fontWeight: 700,
              color: '#0b2545',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
          >
            {field.name}
          </div>
          {Array.isArray(field.value) ? (
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              {field.value.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ol>
          ) : (
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                paddingLeft: '8px',
                borderLeft: '2px solid #0b2545',
              }}
            >
              {field.value}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Older BVIs can have no seller linked (the user was removed), so they are
// keyed by position instead of crashing the page.
const bviKey = (entry, index) => entry?.seller?._id || `no-seller-${index}`;

const BviViewer = ({ listingId, companyName }) => {
  const { user, access_token: accessToken } = useSelector(
    (state) => state?.authReducer || {},
  );

  const [loading, setLoading] = useState(false);
  const [bvis, setBvis] = useState([]);
  const [selectedBvi, setSelectedBvi] = useState(null);
  const [parsedBvi, setParsedBvi] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [isOpenBVIEditor, setIsOpenBVIEditor] = useState('');
  const [showSureModal, setShowSureModal] = useState(false);

  const printRef = useRef(null);

  const sellerName = selectedBvi?.seller
    ? `${selectedBvi?.seller?.firstName || ''} ${
        selectedBvi?.seller?.lastName || ''
      }`.trim()
    : '';

  const downloadBvi = useReactToPrint({
    contentRef: printRef,
    documentTitle: [companyName, sellerName, 'BVI']
      .filter(Boolean)
      .join(' - '),
    pageStyle: '@page { size: A4; margin: 16mm; }',
  });

  const getBvis = async () => {
    const url = BaseURL(`business/${listingId}/admin/bvi`);

    setLoading(true);
    const response = await Get(url, accessToken);
    setLoading(false);

    if (response?.data) {
      let data = [];
      if (Array.isArray(response?.data?.noteBox)) {
        data = response?.data?.noteBox;
      } else if (response?.data?.noteBox && response?.data?.owner?.length) {
        data = [
          {
            seller: response?.data?.owner[0],
            bvi: response?.data?.noteBox,
          },
        ];
      }
      setBvis(data);
      if (data?.length && !selectedBvi) setSelectedBvi(data[0]);
    }
  };

  const updateBvi = async (seller, bvi) => {
    const url = BaseURL(`business/${listingId}/admin/bvi`);
    setIsEditing(true);
    const response = await Post(
      url,
      { seller: seller?._id, bvi },
      apiHeader(accessToken),
    );
    setIsEditing(false);
    if (response?.data) {
      toast.success(
        `BVI ${isOpenBVIEditor == 'create' ? 'saved' : 'updated'} successfully`,
      );
      setSelectedBvi({ seller, bvi });
      setIsOpenBVIEditor('');
      getBvis();
    }
  };

  const deleteBvi = async () => {
    if (!selectedBvi) return;
    const url = BaseURL(
      `business/${listingId}/admin/bvi/${selectedBvi?.seller?._id}`,
    );
    setIsEditing(true);
    const response = await Delete(url, null, apiHeader(accessToken));
    setIsEditing(false);
    if (response?.data) {
      toast.success('BVI deleted successfully');
      setIsOpenBVIEditor('');
      const nBvis = bvis.filter(
        (ele) => ele?.seller?._id != selectedBvi?.seller?._id,
      );
      setBvis(nBvis);
      nBvis?.length > 0 ? setSelectedBvi(nBvis[0]) : setSelectedBvi(null);
      setShowSureModal(false);
    }
  };

  useEffect(() => {
    getBvis();
  }, []);

  useEffect(() => {
    if (selectedBvi) {
      setParsedBvi(parseNoteBox(selectedBvi?.bvi));
    }
  }, [selectedBvi]);

  return (
    <>
      <div className={[classes.mainContainer]}>
        <div className={[classes.headerContainer]}>
          {selectedBvi && (
            <TextField
              name="seller"
              label="Seller"
              select
              sx={{
                width: '200px',
                textTransform: 'capitalize',
              }}
              value={bviKey(selectedBvi, bvis.indexOf(selectedBvi))}
              onChange={(e) => {
                setSelectedBvi(
                  bvis.find((ele, i) => bviKey(ele, i) == e.target.value),
                );
              }}
            >
              {bvis.map((r, i) => (
                <MenuItem
                  key={bviKey(r, i)}
                  value={bviKey(r, i)}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {r?.seller
                    ? `${r.seller.firstName || ''} ${r.seller.lastName || ''}`
                    : 'Older BVI, no seller linked'}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            label={'Create'}
            onClick={() => {
              setIsOpenBVIEditor('create');
            }}
          />
          {selectedBvi && (
            <Button
              label={'Edit'}
              onClick={() => {
                setIsOpenBVIEditor('edit');
              }}
            />
          )}
          {selectedBvi && (
            <Button
              label={'Delete'}
              onClick={() => {
                setShowSureModal(true);
              }}
            />
          )}
          {selectedBvi && parsedBvi?.length > 0 && (
            <Button
              label={'Download'}
              onClick={() => downloadBvi()}
            />
          )}
        </div>
        {loading ? (
          <Loader />
        ) : !parsedBvi || !parsedBvi?.length ? (
          <NoData />
        ) : (
          <div>
            {parsedBvi?.map((field) => (
              <FieldSection name={field.name} value={field.value} />
            ))}
          </div>
        )}

        {/* Off-screen printable layout used to download the selected BVI as PDF */}
        <div style={{ overflow: 'hidden', height: 0 }}>
          <PrintableBvi
            innerRef={printRef}
            companyName={companyName}
            seller={selectedBvi?.seller}
            fields={parsedBvi}
          />
        </div>

        {isOpenBVIEditor && (
          <BVIEditorModal
            show={isOpenBVIEditor}
            setShow={setIsOpenBVIEditor}
            isSaving={isEditing}
            handleSubmit={updateBvi}
            title={isOpenBVIEditor == 'create' ? 'Create BVI' : 'Edit BVI'}
            data={isOpenBVIEditor == 'create' ? null : selectedBvi}
          />
        )}

        {showSureModal && (
          <AreYouSureModal
            isApiCall={isEditing}
            setShow={setShowSureModal}
            show={showSureModal}
            onClick={deleteBvi}
            subTitle={`Do you want to delete this BVI?`}
          />
        )}
      </div>
    </>
  );
};

export default BviViewer;
